import { useEffect, useRef, useState } from "react";
import { AlertCircle, CalendarDays, Check, ChevronDown, Minus, Search, X } from "lucide-react";
import dayjs from "dayjs";
import { es } from "date-fns/locale";
import type { DateRange } from "react-day-picker";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { hasTickets } from "@/utils/pipelines";
import { pipelineAccent } from "./types";
import {
  groupCheckState,
  matchedGroupLabel,
  toggleGroup,
  type FilterGroup,
  type GroupCheckState,
} from "@/utils/employeeGroups";
import type { SolicitudFiltros } from "./solicitudes/types";
import type { TableroFiltros } from "@/containers/app/tablero/TableroContainer";

type Option = { id: string; label: string };

// Ticket priority is a fixed enum on the backend (ticketPrioritySchema), so
// unlike categoría it needs no query.
const PRIORIDAD_OPTIONS: Option[] = [
  { id: "urgent", label: "Urgente" },
  { id: "high", label: "Alta" },
  { id: "medium", label: "Media" },
  { id: "low", label: "Baja" },
];

type Props = {
  pipelines: Option[];
  pipelineId: string;
  onPipelineChange: (id: string) => void;
  vista: "kanban" | "lista";
  setVista: (v: "kanban" | "lista") => void;
  tab: "activos" | "inactivos";
  setTab: (t: "activos" | "inactivos") => void;
  modo: "leads" | "solicitudes";
  setModo: (m: "leads" | "solicitudes") => void;
  solicitudFiltros: SolicitudFiltros;
  setSolicitudFiltros: (f: SolicitudFiltros) => void;
  // Solicitudes search box (name / phone / cédula) — separate state from the
  // lead-only `filtros.query`.
  solicitudQuery: string;
  setSolicitudQuery: (v: string) => void;
  categorias: Option[];
  filtros: TableroFiltros;
  setFiltros: (f: TableroFiltros) => void;
  dateRange: DateRange | undefined;
  setDateRange: (r: DateRange | undefined) => void;
  asesores: Option[];
  // Saved presets shown above the asesores in the same dropdown; already
  // narrowed to active members by the container.
  grupos: FilterGroup[];
  etapas: Option[];
  fuentes: Option[];
  pautas: Option[];
  etiquetas: Option[];
  anyFilter: boolean;
  onClear: () => void;
  realtime: boolean;
  setRealtime: (v: boolean) => void;
  /** A search is running, so the real-time switch has nothing to govern. */
  searchActive: boolean;
  realtimeLocked: boolean;
  showAsesorFilter: boolean;
  // Data-scoped employees are locked to their linked asesores' leads and get no
  // Inactivos tab (the inactive store has no employee scoping).
  isDataScoped: boolean;
};

const Chip = ({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick?: () => void;
}) => (
  <button
    onClick={onClick}
    className={cn(
      "h-8 rounded-pill border px-3 text-sm font-medium transition-colors",
      active
        ? "border-palomma-primary bg-palomma-primary text-white"
        : "border-transparent bg-transparent text-neutral-600 hover:bg-neutral-100"
    )}
  >
    {label}
  </button>
);

// Lists shorter than this are faster to scan than to type into; asesor lists at
// bigger merchants blow past it, which is the case the search box exists for.
const SEARCHABLE_THRESHOLD = 8;

const normalize = (value: string) =>
  value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

const matchOptions = (options: Option[], query: string) => {
  const needle = normalize(query.trim());
  if (!needle) return options;
  return options.filter((o) => normalize(o.label).includes(needle));
};

// A group also matches on its members, so typing an asesor's name surfaces the
// groups they belong to alongside their own row.
const matchGroups = (groups: FilterGroup[], options: Option[], query: string) => {
  const needle = normalize(query.trim());
  if (!needle) return groups;
  const labelById = new Map(options.map((o) => [o.id, o.label]));
  return groups.filter(
    (g) =>
      normalize(g.label).includes(needle) ||
      g.employeeIds.some((id) => normalize(labelById.get(id) ?? "").includes(needle))
  );
};

// Radix focuses the content (or its first item) when a menu opens, so the input
// grabs focus right after that lands — same trick as the chat EmployeePicker.
// Keystrokes stop here so the menu's typeahead doesn't swallow them mid-word;
// ArrowDown and Escape still bubble so keyboard nav and close keep working.
const DropdownSearch = ({
  inputRef,
  label,
  value,
  onChange,
  onEnter,
}: {
  inputRef: React.RefObject<HTMLInputElement>;
  label: string;
  value: string;
  onChange: (v: string) => void;
  onEnter: () => void;
}) => {
  useEffect(() => {
    const id = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(id);
  }, [inputRef]);

  return (
    <div className="relative p-1 pb-1.5">
      <Search className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-600" />
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" || e.key === "Escape") return;
          e.stopPropagation();
          if (e.key === "Enter") {
            e.preventDefault();
            onEnter();
          }
        }}
        placeholder={`Buscar ${label.toLowerCase()}…`}
        aria-label={`Buscar ${label.toLowerCase()}`}
        className="h-8 w-full rounded-md border border-neutral-300 bg-surface pl-8 pr-2 text-sm focus:outline-none focus:ring-2 focus:ring-palomma-primary/30"
      />
    </div>
  );
};

// Radix focuses whatever item the cursor crosses, which would steal the caret
// out of the search box and route the next keystroke into the menu's typeahead.
// Preventing the pointer-move default keeps focus in the input; hover feedback
// moves to :hover since items no longer focus on hover.
const searchableItemProps = {
  onPointerMove: (e: React.PointerEvent) => e.preventDefault(),
  className: "hover:bg-accent hover:text-accent-foreground",
};

const NoMatches = ({ query }: { query: string }) => (
  <p className="px-2 py-6 text-center text-sm text-neutral-600">
    Sin coincidencias para «{query.trim()}»
  </p>
);

const FilterDropdown = ({
  label,
  value,
  options,
  onSelect,
}: {
  label: string;
  value: string | null;
  options: Option[];
  onSelect: (v: string | null) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const activeLabel = options.find((o) => o.id === value)?.label;
  const searchable = options.length > SEARCHABLE_THRESHOLD;
  const visible = searchable ? matchOptions(options, query) : options;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setQuery("");
  };

  const handleSelect = (id: string | null) => {
    onSelect(id);
    handleOpenChange(false);
  };

  // Enter picks the top match, so typing a name and hitting Enter is a
  // complete interaction without ever touching the list.
  const handleEnter = () => {
    const top = visible[0];
    if (top) handleSelect(top.id === value ? null : top.id);
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            "h-8 rounded-pill border px-3 text-sm font-medium",
            value
              ? "border-palomma-primary/30 bg-palomma-primary/10 text-palomma-primary"
              : "border-neutral-300 bg-surface text-neutral-600 hover:bg-neutral-100"
          )}
        >
          {activeLabel ?? label} ▾
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className={cn("p-0", searchable && "w-[240px]")}>
        {searchable ? (
          <DropdownSearch
            inputRef={inputRef}
            label={label}
            value={query}
            onChange={setQuery}
            onEnter={handleEnter}
          />
        ) : null}
        <div
          className={cn(
            "overflow-y-auto p-1",
            searchable ? "max-h-[280px] pt-0" : "max-h-[320px]"
          )}
        >
          <DropdownMenuItem
            onClick={() => handleSelect(null)}
            {...(searchable ? searchableItemProps : {})}
          >
            Todos
          </DropdownMenuItem>
          {visible.map((o) => (
            <DropdownMenuCheckboxItem
              key={o.id}
              checked={value === o.id}
              onCheckedChange={() => handleSelect(value === o.id ? null : o.id)}
              {...(searchable ? searchableItemProps : {})}
            >
              {o.label}
            </DropdownMenuCheckboxItem>
          ))}
          {visible.length === 0 ? <NoMatches query={query} /> : null}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Sentence case, label type — the section headers separate saved groups from
// individual asesores without shouting (DESIGN.md forbids ALL CAPS in copy).
const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <DropdownMenuLabel className="px-2 py-1 text-xs font-medium text-neutral-600">
    {children}
  </DropdownMenuLabel>
);

// A group row is a plain item, not a DropdownMenuCheckboxItem: that primitive
// hardcodes a Check inside Radix's ItemIndicator, which also renders for
// "indeterminate" — a half-selected group would look fully selected. Rendering
// the three states by hand keeps the shared shadcn primitive untouched.
const GroupRow = ({
  group,
  state,
  onToggle,
  searchable,
}: {
  group: FilterGroup;
  state: GroupCheckState;
  onToggle: () => void;
  searchable: boolean;
}) => (
  <DropdownMenuItem
    inset
    disabled={!group.employeeIds.length}
    onClick={onToggle}
    onSelect={(e) => e.preventDefault()}
    {...(searchable ? searchableItemProps : {})}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      {state === "all" ? (
        <Check className="h-4 w-4" />
      ) : state === "some" ? (
        <Minus className="h-4 w-4" />
      ) : null}
    </span>
    <span className="flex-1">{group.label}</span>
    <span className="ml-3 text-xs text-neutral-600">
      {group.employeeIds.length || "sin asesores"}
    </span>
  </DropdownMenuItem>
);

// Asesor: multi-select (Tinybird queries with IN); the real-time index path
// takes one employeeId, so selecting more than one flips the board to
// non-realtime mode. The dropdown stays open on toggle. Groups are a saved
// preset rendered above the individual asesores — picking one just checks its
// members, so there is no separate group filter state.
const MultiFilterDropdown = ({
  label,
  values,
  options,
  groups,
  onChange,
}: {
  label: string;
  values: string[];
  options: Option[];
  groups?: FilterGroup[];
  onChange: (v: string[]) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const groupList = groups ?? [];
  // Naming the group beats "6 asesores" when the selection is exactly one.
  const activeLabel =
    matchedGroupLabel(groupList, values) ??
    (values.length === 1
      ? options.find((o) => o.id === values[0])?.label
      : values.length > 1
        ? `${values.length} ${label.toLowerCase()}${/[aeiou]$/i.test(label) ? "s" : "es"}`
        : undefined);
  const searchable = options.length + groupList.length > SEARCHABLE_THRESHOLD;
  const visible = searchable ? matchOptions(options, query) : options;
  const visibleGroups = searchable ? matchGroups(groupList, options, query) : groupList;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setQuery("");
  };

  const handleToggle = (id: string) =>
    onChange(values.includes(id) ? values.filter((v) => v !== id) : [...values, id]);

  const handleToggleGroup = (group: FilterGroup) => onChange(toggleGroup(group, values));

  // Enter toggles the top match and clears the query so several asesores can be
  // stacked up without leaving the keyboard; the menu stays open like a click.
  // Asesores win over groups — a name typed in full should check that person.
  const handleEnter = () => {
    const top = visible[0];
    if (top) {
      handleToggle(top.id);
      setQuery("");
      return;
    }
    const topGroup = visibleGroups.find((g) => g.employeeIds.length);
    if (!topGroup) return;
    handleToggleGroup(topGroup);
    setQuery("");
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            "h-8 rounded-pill border px-3 text-sm font-medium",
            values.length
              ? "border-palomma-primary/30 bg-palomma-primary/10 text-palomma-primary"
              : "border-neutral-300 bg-surface text-neutral-600 hover:bg-neutral-100"
          )}
        >
          {activeLabel ?? label} ▾
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className={cn("p-0", searchable && "w-[240px]")}>
        {searchable ? (
          <DropdownSearch
            inputRef={inputRef}
            label={label}
            value={query}
            onChange={setQuery}
            onEnter={handleEnter}
          />
        ) : null}
        <div
          className={cn(
            "overflow-y-auto p-1",
            searchable ? "max-h-[280px] pt-0" : "max-h-[320px]"
          )}
        >
          <DropdownMenuItem
            onClick={() => onChange([])}
            {...(searchable ? searchableItemProps : {})}
          >
            Todos
          </DropdownMenuItem>
          {visibleGroups.length ? (
            <>
              <SectionLabel>Grupos</SectionLabel>
              {visibleGroups.map((g) => (
                <GroupRow
                  key={g.id}
                  group={g}
                  state={groupCheckState(g, values)}
                  onToggle={() => handleToggleGroup(g)}
                  searchable={searchable}
                />
              ))}
              <DropdownMenuSeparator />
              <SectionLabel>Asesores</SectionLabel>
            </>
          ) : null}
          {visible.map((o) => (
            <DropdownMenuCheckboxItem
              key={o.id}
              checked={values.includes(o.id)}
              onCheckedChange={() => handleToggle(o.id)}
              onSelect={(e) => e.preventDefault()}
              {...(searchable ? searchableItemProps : {})}
            >
              {o.label}
            </DropdownMenuCheckboxItem>
          ))}
          {visible.length === 0 && visibleGroups.length === 0 ? (
            <NoMatches query={query} />
          ) : null}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Prototype-style pipeline switcher: colored dot + tinted active pill.
// Hidden when the merchant only has one pipeline to offer.
const PipelineSwitcher = ({
  pipelines,
  pipelineId,
  onPipelineChange,
}: {
  pipelines: Option[];
  pipelineId: string;
  onPipelineChange: (id: string) => void;
}) => {
  if (pipelines.length <= 1) return null;
  const accent = pipelineAccent(pipelineId);
  const activeLabel = pipelines.find((p) => p.id === pipelineId)?.label ?? pipelineId;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            "flex h-8 items-center gap-2 rounded-pill border px-3 text-sm font-medium transition-colors",
            accent.active
          )}
        >
          <span className={cn("h-2 w-2 rounded-full", accent.dot)} />
          {activeLabel}
          <ChevronDown className="h-3.5 w-3.5 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[180px]">
        {pipelines.map((p) => (
          <DropdownMenuItem
            key={p.id}
            onClick={() => onPipelineChange(p.id)}
            className={cn("gap-2", pipelineId === p.id && pipelineAccent(p.id).active)}
          >
            <span className={cn("h-2 w-2 rounded-full", pipelineAccent(p.id).dot)} />
            {p.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const SubHeaderFiltros = ({
  pipelines,
  pipelineId,
  onPipelineChange,
  vista,
  setVista,
  tab,
  setTab,
  modo,
  setModo,
  solicitudFiltros,
  setSolicitudFiltros,
  solicitudQuery,
  setSolicitudQuery,
  categorias,
  filtros,
  setFiltros,
  dateRange,
  setDateRange,
  asesores,
  grupos,
  etapas,
  fuentes,
  pautas,
  etiquetas,
  anyFilter,
  onClear,
  realtime,
  setRealtime,
  searchActive,
  realtimeLocked,
  showAsesorFilter,
  isDataScoped,
}: Props) => {
  // Everything below the pipeline switcher except the date picker is a lead
  // concept; Solicitudes swaps in its own filters (and has no real-time or
  // lista path at all), so the lead controls hide wholesale.
  const isLeads = modo === "leads";
  const showLeadControls = tab === "activos" && isLeads;
  // Search is the one lead control that also works on Inactivos. Both tabs now
  // resolve a phone or a WhatsApp handle against DynamoDB, so the affordance is
  // the same on either — no `tel` keyboard, which would block typing a username.
  const showSearch = isLeads;

  // Activos filters lastLeadMessageAt (gsi1); Inactivos filters creation date
  // (createdAfter/createdBefore on the non-realtime store); Solicitudes filters
  // ticket createdAt — the label states which.
  const dateEmptyLabel =
    !isLeads || tab === "inactivos" ? "Fecha de creación" : "Última actividad";
  const dateLabel =
    dateRange?.from && dateRange?.to
      ? `${dayjs(dateRange.from).format("DD MMM")} – ${dayjs(dateRange.to).format("DD MMM")}`
      : dateRange?.from
        ? dayjs(dateRange.from).format("DD MMM")
        : dateEmptyLabel;

  return (
    <>
      <div className="flex min-h-12 flex-wrap items-center gap-3 border-b border-neutral-300 bg-surface px-4 py-2">
        <h1 className="text-base font-semibold text-neutral-900">Tablero</h1>
        <PipelineSwitcher
          pipelines={pipelines}
          pipelineId={pipelineId}
          onPipelineChange={onPipelineChange}
        />
        {/* Non-default pipelines have no Inactivos tab — inactive is a board
            column there, so the toggle only renders for Comercial. Data-scoped
            employees never get it: the inactive store has no employee scoping. */}
        {pipelineId === "default" && !isDataScoped ? (
          <div className="flex items-center gap-1 rounded-pill border border-neutral-300 p-0.5">
            {(["activos", "inactivos"] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={cn(
                  "h-7 rounded-pill px-3 text-xs font-medium capitalize transition-colors",
                  tab === t
                    ? "bg-palomma-primary text-white"
                    : "text-neutral-600 hover:bg-neutral-100"
                )}
              >
                {t}
              </button>
            ))}
          </div>
        ) : null}
        {/* Ticket pipelines get Leads/Solicitudes where Comercial gets
            Activos/Inactivos — the two are mutually exclusive, so they share
            this slot. Hidden for data-scoped employees: the serviceRequests
            procedures have no employee scoping. */}
        {hasTickets(pipelineId) && !isDataScoped ? (
          <div className="flex items-center gap-1 rounded-pill border border-neutral-300 p-0.5">
            {(
              [
                ["leads", "Leads"],
                ["solicitudes", "Solicitudes"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                onClick={() => setModo(value)}
                className={cn(
                  "h-7 rounded-pill px-3 text-xs font-medium transition-colors",
                  modo === value
                    ? "bg-palomma-primary text-white"
                    : "text-neutral-600 hover:bg-neutral-100"
                )}
              >
                {label}
              </button>
            ))}
          </div>
        ) : null}
        {showLeadControls ? (
          <div className="flex items-center gap-1 rounded-pill bg-neutral-100 p-1">
            <Chip label="Kanban" active={vista === "kanban"} onClick={() => setVista("kanban")} />
            <Chip label="Lista" active={vista === "lista"} onClick={() => setVista("lista")} />
          </div>
        ) : null}
        {showSearch ? (
          <div className="relative ml-auto min-w-[200px] max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-600" />
            <input
              value={filtros.query}
              onChange={(e) => setFiltros({ ...filtros, query: e.target.value })}
              aria-label="Buscar leads por nombre, teléfono o usuario"
              placeholder={
                tab === "inactivos"
                  ? "Buscar por teléfono o usuario…"
                  : "Buscar por nombre, teléfono o usuario…"
              }
              className="h-9 w-full rounded-md border border-neutral-300 bg-surface pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-palomma-primary/30"
            />
          </div>
        ) : null}
        {/* Solicitudes has its own search box (name / phone / cédula), on its
            own state — not gated by the lead-only showSearch. */}
        {!isLeads ? (
          <div className="relative ml-auto min-w-[200px] max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-600" />
            <input
              value={solicitudQuery}
              onChange={(e) => setSolicitudQuery(e.target.value)}
              placeholder="Buscar por nombre, teléfono o cédula…"
              className="h-9 w-full rounded-md border border-neutral-300 bg-surface pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-palomma-primary/30"
            />
          </div>
        ) : null}
      </div>

      <div className="flex min-h-12 flex-wrap items-center gap-2 border-b border-neutral-300 bg-surface px-4 py-2">
        {showLeadControls ? (
          <>
            {showAsesorFilter ? (
              <MultiFilterDropdown
                label="Asesor"
                values={filtros.asesores}
                options={asesores}
                groups={grupos}
                onChange={(v) => setFiltros({ ...filtros, asesores: v })}
              />
            ) : null}
            <FilterDropdown
              label="Etapa"
              value={filtros.etapa}
              options={etapas}
              onSelect={(v) => setFiltros({ ...filtros, etapa: v })}
            />
          </>
        ) : null}
        {/* Fuente, pauta and atención humana are filter expressions, not index
            key conditions — only the non-realtime store can serve them, so
            selecting any of them on Activos flips the board to non-realtime
            mode. None exists on the ticket store. */}
        {isLeads ? (
          <>
            <FilterDropdown
              label="Fuente"
              value={filtros.fuente}
              options={fuentes}
              onSelect={(v) => setFiltros({ ...filtros, fuente: v })}
            />
            {pautas.length > 0 ? (
              <FilterDropdown
                label="Pauta"
                value={filtros.pauta}
                options={pautas}
                onSelect={(v) => setFiltros({ ...filtros, pauta: v })}
              />
            ) : null}
            {etiquetas.length > 0 ? (
              <MultiFilterDropdown
                label="Etiqueta"
                values={filtros.tagIds}
                options={etiquetas}
                onChange={(v) => setFiltros({ ...filtros, tagIds: v })}
              />
            ) : null}
          </>
        ) : (
          <>
            {!isDataScoped ? (
              <FilterDropdown
                label="Asesor"
                value={solicitudFiltros.asesor}
                options={asesores}
                onSelect={(v) => setSolicitudFiltros({ ...solicitudFiltros, asesor: v })}
              />
            ) : null}
            <FilterDropdown
              label="Categoría"
              value={solicitudFiltros.categoria}
              options={categorias}
              onSelect={(v) => setSolicitudFiltros({ ...solicitudFiltros, categoria: v })}
            />
            <FilterDropdown
              label="Prioridad"
              value={solicitudFiltros.prioridad}
              options={PRIORIDAD_OPTIONS}
              onSelect={(v) => setSolicitudFiltros({ ...solicitudFiltros, prioridad: v })}
            />
          </>
        )}

        <Popover>
          <PopoverTrigger asChild>
            <button
              className={cn(
                "flex h-8 items-center gap-1.5 rounded-pill border px-3 text-sm font-medium",
                dateRange?.from
                  ? "border-palomma-primary/30 bg-palomma-primary/10 text-palomma-primary"
                  : "border-neutral-300 bg-surface text-neutral-600 hover:bg-neutral-100"
              )}
            >
              <CalendarDays className="h-3.5 w-3.5" />
              {dateLabel}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              selected={dateRange}
              onSelect={setDateRange}
              locale={es}
              defaultMonth={dateRange?.from}
              numberOfMonths={2}
            />
            {dateRange?.from ? (
              <div className="border-t border-neutral-100 p-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-neutral-600"
                  onClick={() => setDateRange(undefined)}
                >
                  Quitar fechas
                </Button>
              </div>
            ) : null}
          </PopoverContent>
        </Popover>

        {showLeadControls ? (
          <button
            onClick={() =>
              setFiltros({ ...filtros, soloAtencionHumana: !filtros.soloAtencionHumana })
            }
            className={cn(
              "flex h-8 items-center gap-1.5 rounded-pill border px-3 text-sm font-medium transition-colors",
              filtros.soloAtencionHumana
                ? "border-danger bg-danger text-white"
                : "border-danger/40 bg-surface text-danger hover:bg-danger/10"
            )}
            title="Mostrar solo leads que requieren atención humana"
          >
            <AlertCircle className="h-3.5 w-3.5" />
            Requieren atención humana
          </button>
        ) : null}

        {anyFilter ? (
          <Button variant="ghost" size="sm" onClick={onClear} className="h-8 text-neutral-600">
            <X className="mr-1 h-4 w-4" /> Limpiar filtros
          </Button>
        ) : null}

        {showLeadControls ? (
          // Inert during a search: the results come from both stores at once and
          // each row says which one it came from, so the switch has nothing to
          // govern. Disabled rather than hidden, so it doesn't appear to have
          // been turned off.
          <label
            className={cn(
              "ml-auto flex items-center gap-2",
              searchActive || realtimeLocked ? "cursor-default opacity-50" : "cursor-pointer"
            )}
            title={
              searchActive
                ? "Durante una búsqueda los resultados vienen de ambas fuentes; cada fila indica cuál"
                : realtimeLocked
                  ? "Elige un solo asesor para ver el tablero en tiempo real"
                  : "Se desactiva automáticamente con filtros que requieren datos sin tiempo real"
            }
          >
            <span className="text-xs font-medium text-neutral-600">Tiempo real</span>
            <Switch
              checked={realtime}
              onCheckedChange={setRealtime}
              disabled={searchActive || realtimeLocked}
            />
          </label>
        ) : null}
      </div>
    </>
  );
};

export default SubHeaderFiltros;
